import { useSearchParams } from 'react-router-dom'
import AdyenCheckout from '@adyen/adyen-web'
import config from '../../config'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Banner } from '@ordr-as/umami-react'
import Container from '../../components/layout/Container'
import {
    useLogApplePayCheckoutAuthorisePayment,
    useLogApplePayCheckoutCancelPayment,
    useLogApplePayCheckoutClickBackButton,
    useLogApplePayCheckoutLoadPage,
    useLogApplePayCheckoutRedirectToAction,
    useLogApplePayCheckoutRedirectToReturnUrl,
} from 'utils/analytics'
import { authorisePayment, cancelPayment } from 'api/paymentApi'
import ApplePayElement from '@adyen/adyen-web/dist/types/components/ApplePay'
import './style/applePay.css'
import { AuthorisePaymentRequest } from '../../../generated-api'
const ApplePayCheckout = () => {
    const [searchParams] = useSearchParams()
    const [applePayIsUnavailable, setApplePayIsUnavailable] = useState(false)
    const [hasSubmitted, setHasSubmitted] = useState(false)
    const sessionId = searchParams.get('sessionId')
    const sessionData = searchParams.get('sessionData')
    const paymentReference = searchParams.get('paymentReference')!
    const hasRequiredParams = sessionId && sessionData && paymentReference

    // Analytics
    const logLoadPage = useLogApplePayCheckoutLoadPage()
    const logClickBackButton = useLogApplePayCheckoutClickBackButton()
    const logCancelPayment = useLogApplePayCheckoutCancelPayment()
    const logAuthorisePayment = useLogApplePayCheckoutAuthorisePayment()
    const logRedirectToReturnUrl = useLogApplePayCheckoutRedirectToReturnUrl()
    const logRedirectToAction = useLogApplePayCheckoutRedirectToAction()

    const applePayComponent = useRef<ApplePayElement>()

    const onBackButtonClicked = async (): Promise<boolean> => {
        logClickBackButton(paymentReference)

        if (!hasSubmitted) return new Promise(() => false)

        logCancelPayment(paymentReference, 'fetch')

        return cancelPayment(paymentReference!).then(() => true)
    }

    const onSubmitPayment = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (state: any, component: any) => {
            component.setStatus('loading')

            if (!state.isValid) {
                alert('Invalid card details. Please make sure you have entered the correct information.')
                return
            }

            const request = {
                paymentMethod: {
                    type: state.data.paymentMethod.type,
                    applePayToken: state.data.paymentMethod.applePayToken,
                },
                origin: window.location.origin,
            } as AuthorisePaymentRequest
            logAuthorisePayment(paymentReference)

            setHasSubmitted(true)

            const response = await authorisePayment(paymentReference, request)

            // Payment was authorised or refused, so we want to redirect to the returnUrl.
            // The returnUrl in the response here will contain either the returnUrl or failureReturnUrl which was provided
            // in the request when initiating the payment, depending on whether the payment was authorised or refused.
            if (response.isPaymentResultAvailable) {
                logRedirectToReturnUrl(paymentReference)

                console.log('Redirecting to returnUrl: ', response.returnUrl)
                window.location.href = response.returnUrl!
                return
            }

            logRedirectToAction(paymentReference)

            component.handleAction(response.action)
        },
        [logAuthorisePayment, logRedirectToAction, logRedirectToReturnUrl, paymentReference]
    )

    const createCheckoutConfig = useCallback(
        (sessionId: string, sessionData: string) => {
            return {
                environment: config.adyen.env,
                clientKey: config.adyen.clientKey,
                session: {
                    id: sessionId,
                    sessionData,
                },
                paymentMethodsConfiguration: {
                    applepay: {
                        buttonType: 'buy',
                    },
                },
                onSubmit: (state: unknown, component: React.ReactNode) => onSubmitPayment(state, component),
            }
        },
        [onSubmitPayment]
    )

    useEffect(() => {
        if (hasRequiredParams) {
            console.log('Initializing Checkout')

            const checkoutConfig = createCheckoutConfig(sessionId!, sessionData!)

            AdyenCheckout(checkoutConfig)
                .then((checkout) => {
                    if (applePayComponent) {
                        applePayComponent.current = checkout.create('applepay')

                        applePayComponent.current
                            .isAvailable()
                            .then(() => {
                                if (applePayComponent.current) {
                                    applePayComponent.current.mount('#apple-pay-checkout-container')
                                }
                            })
                            .catch((e: unknown) => {
                                setApplePayIsUnavailable(true)
                                console.log('Apple Pay is not available. Error: ', e)
                            })
                    }
                })
                .catch((e: unknown) => {
                    setApplePayIsUnavailable(true)
                    console.log('Apple Pay is not available. Error: ', e)
                })
        }
    }, [createCheckoutConfig, hasRequiredParams, sessionData, sessionId])

    useEffect(() => {
        logLoadPage(paymentReference)
    }, [logLoadPage, paymentReference])

    return (
        <Container className="flex flex-col justify-center h-full" beforeNavigateBack={onBackButtonClicked}>
            <h1 className="mb-3 text-center">Pay with Apple Pay</h1>

            {hasRequiredParams ? (
                <>
                    {applePayIsUnavailable ? (
                        <div>The current operating system or devices does not support Apple Pay</div>
                    ) : (
                        <div id="apple-pay-checkout-container" className=" text-center" />
                    )}
                </>
            ) : (
                <Banner kind="warn" stretch="parent" onClose={() => null}>
                    Session ID or session data is missing
                </Banner>
            )}
        </Container>
    )
}

export default ApplePayCheckout
